/* @jsx h */
import {
  computed,
  defineComponent,
  h,
  onMounted,
  provide,
  ref,
  watch,
} from "vue";
import { useQuery } from "@tanstack/vue-query";
import {
  DateRangePicker,
  ListMultiSelect,
  getDateRange,
  useLocalization,
  type DateRange,
} from "@lemmonhq/uikit";
import { formatPriceInLocale, setTitle } from "@lemmonhq/orders";
import type { TopProductsDatum } from "@lemmonhq/types";
import { Loading } from "@app/vue/components/UIComponents";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useGroupAuth } from "@app/vue/providers/GroupAuth";
import { reportingShops } from "../../../reportingShops";
import { OverviewChart } from "@app/vue/pages/Reports/subpages/Overview/OverviewChart";
import { MobileOverviewHeader } from "@app/vue/pages/Reports/subpages/Overview/components/MobileOverviewHeader";

export const GroupOverviewDashboard = defineComponent({
  name: "GroupOverviewDashboard",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const { session } = useGroupAuth();
    const initialRange = getDateRange("today");
    const dateRange = ref<DateRange>({
      type: "today",
      startDate: initialRange.startDate,
      endDate: initialRange.endDate,
    });
    const mobileRevenueScrollProgress = ref(0);
    const selectedRestaurantIds = ref<number[]>([]);
    provide("mobileRevenueScrollProgress", mobileRevenueScrollProgress);

    onMounted(() => setTitle(t("admin.overview.title")));

    const shopsQuery = useQuery({
      queryKey: ["group", "shops"],
      queryFn: () => trpc.shops.list.query(),
    });
    const managerShops = computed(() =>
      reportingShops(shopsQuery.data.value ?? [], session.value.group_role),
    );
    const restaurantItems = computed(() =>
      managerShops.value.map((shop) => ({ id: shop.id, name: shop.name })),
    );
    const selectionInitialized = ref(false);
    watch(
      managerShops,
      (shops) => {
        if (!selectionInitialized.value && shops.length > 0) {
          selectedRestaurantIds.value = shops.map((shop) => shop.id);
          selectionInitialized.value = true;
        }
      },
      { immediate: true },
    );

    const overviewQuery = useQuery({
      queryKey: computed(() => [
        "group",
        "overview",
        dateRange.value.startDate,
        dateRange.value.endDate,
        [...selectedRestaurantIds.value].sort((a, b) => a - b).join(","),
      ]),
      queryFn: () =>
        trpc.groupReports.overview.query({
          startDate: dateRange.value.startDate,
          endDate: dateRange.value.endDate,
          restaurantIds: selectedRestaurantIds.value,
        }),
    });

    const updateDateRange = (nextRange: DateRange) => {
      dateRange.value = nextRange;
    };

    return () => {
      const report = overviewQuery.data.value;
      const okReport = report?.status === "ok" ? report : null;
      const products =
        okReport?.top_products.map((row) => ({
          product: {
            id: row.product_id,
            name: row.product_name,
          } as TopProductsDatum["product"],
          total_count: row.total_count,
          total_revenue: row.total_revenue,
          restaurant_name: row.restaurant_name,
        })) ?? [];

      return (
        <div class="w-full flex flex-col overflow-auto">
          <MobileOverviewHeader
            dateRange={dateRange.value}
            onDateRangeChange={updateDateRange}
            revenue={
              okReport
                ? formatPriceInLocale(
                    okReport.summary.total_amount,
                    false,
                    0,
                    okReport.scope.currency,
                  )
                : undefined
            }
            restaurantName={t("admin.overview.title")}
            canFilter={true}
            currency={okReport?.scope.currency || "CHF"}
            additionalFilters={() => (
              <ListMultiSelect
                items={restaurantItems.value}
                selectedIds={selectedRestaurantIds.value}
                onChange={(ids) => (selectedRestaurantIds.value = ids)}
                loading={shopsQuery.isPending.value}
                buttonLabel={t("admin.dashboard.restaurants")}
                searchPlaceholder={t("admin.dashboard.search_restaurants")}
                compact={false}
                widthClass="w-full"
              />
            )}
          />

          <div class="hidden px-2 py-2 xl:px-4 md:flex flex-col items-start gap-2 lg:flex-row lg:items-center justify-between border-b glossy-bg sticky top-0 z-[1]">
            <div class="flex w-full flex-1 items-center justify-end gap-2 lg:justify-start">
              <DateRangePicker
                className="[&>button]:btn-sm sm:[&>button]:btn-md"
                value={dateRange.value}
                onChange={updateDateRange}
              />
              <ListMultiSelect
                items={restaurantItems.value}
                selectedIds={selectedRestaurantIds.value}
                onChange={(ids) => (selectedRestaurantIds.value = ids)}
                loading={shopsQuery.isPending.value}
                buttonLabel={t("admin.dashboard.restaurants")}
                searchPlaceholder={t("admin.dashboard.search_restaurants")}
                compact={true}
                widthClass="w-auto"
              />
            </div>
            <div class="hidden lg:flex justify-start items-center gap-5">
              <button
                onClick={() => overviewQuery.refetch()}
                class="btn btn-outline btn-sm gap-2"
              >
                <span class="material-symbols-outlined">refresh</span>
              </button>
            </div>
          </div>

          <div class="flex-1 pt-4 px-2 xl:px-4">
            {overviewQuery.isError.value ||
            (!overviewQuery.isPending.value && !report) ? (
              <StatusState
                title={t("admin.overview.error_title")}
                description={t("admin.overview.error_description")}
              />
            ) : report?.status === "no_manager_locations" ? (
              <StatusState
                title={t("admin.overview.no_locations_title")}
                description={t("admin.overview.no_locations_description")}
              />
            ) : report?.status === "mixed_currency" ? (
              <StatusState
                title={t("admin.overview.mixed_currency_title")}
                description={`${t("admin.overview.mixed_currency_description")} ${report.currencies.join(", ")}`}
              />
            ) : okReport ? (
              <OverviewChart
                data={okReport.summary}
                hourlyData={okReport.hourly}
                ordersBreakdown={okReport.orders}
                topProducts={products}
                topCategories={okReport.top_categories}
                loading={overviewQuery.isFetching.value}
                dateRange={dateRange.value}
                currency={okReport.scope.currency}
                showDetailLinks={false}
              />
            ) : (
              <div class="h-full w-full flex items-center justify-center">
                <Loading />
              </div>
            )}
          </div>
        </div>
      );
    };
  },
});

type StatusStateProps = {
  title: string;
  description: string;
};

const StatusState = defineComponent<StatusStateProps>({
  name: "GroupOverviewStatusState",
  props: ["title", "description"],
  setup(props) {
    return () => (
      <div class="h-full w-full flex flex-col space-y-4 items-center justify-center py-20 text-center">
        <div class="text-3xl text-prominent">{props.title}</div>
        <div class="text-1xl text-base-content/70">{props.description}</div>
      </div>
    );
  },
});
